// olli imports
import { olli } from "olli";
import { VegaLiteAdapter } from "olli-adapters"; // Or VegaAdapter, or ObservableAdapter

// vega-lite imports
import * as vega from "vega";
import * as vegaLite from "vega-lite";


// Helper function for capitalising
function toTitleCase(str: string) {
  return str.replace(
    /\w\S*/g,
    function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

// Helper function to take a data set which has been filtered by country/AP and group the studies 
function groupByPublication(filteredDataset: any) {
  // Filter the list by publication ID
  let countryPubIDs = [... new Set(filteredDataset.map(publication => publication['ID (Assigned)']))]
  countryPubIDs = countryPubIDs.map(publication => {
    return { name: publication, data: filteredDataset.filter(datum => datum['ID (Assigned)'] === publication) }
  })
  return countryPubIDs;
}

// Helper function to interpret the AP Types and make them readable
// PAPs* (g = glasses, h = hearing aids, w = wheelchairs, p = prosthetics, PDA = personal digital assistants)
function humaniseAT(types: string) {
  let typeArray = types.split(',');
  let returnedHTML = "<div class='at-tag-container'><h3>Primary Assistive Technologies Measured</h3>";

  for (var i = 0; i < typeArray.length; i++) {
    switch (typeArray[i].trim()) {
      case "G":
        returnedHTML += "<span aria-label='Glasses' class='at-tag g'>Glasses</span>"
        break;
      case "H":
        returnedHTML += "<span aria-label='Hearing Aids' class='at-tag h'>Hearing Aids</span>"
        break;
      case "W":
        returnedHTML += "<span aria-label='Wheelchairs' class='at-tag w'>Wheelchairs</span>"
        break;
      case "P":
        returnedHTML += "<span aria-label='Prosthetics' class='at-tag p'>Prosthetics</span>"
        break;
      case "PDA":
        returnedHTML += "<span aria-label='Personal Digital Assistants' class='at-tag pda'>Personal Digital Assistants</span>"
        break;
      default:
        returnedHTML += "<span aria-label='" + typeArray[i] + "' class='at-tag generic'>" + typeArray[i] + "</span>"
        break;
    }
  }
  returnedHTML += "</div>"
  return returnedHTML;
}

// Helper function to combine and de-dupe all the countries
export function arrayUnique(array) {
  var a = array.concat();
  for (var i = 0; i < a.length; ++i) {
    for (var j = i + 1; j < a.length; ++j) {
      if (a[i] === a[j])
        a.splice(j--, 1);
    }
  }
  return a;
}

export function populatePublicationInfo(contentDiv: HTMLDivElement, calloutDiv: HTMLDivElement, publication: any) {
  // Get first iteration of the study content, to get title etc.
  let contentPub = publication[0];
  let pubTitle = contentPub['Title']
  let participants = contentPub['Participants']
  let link = contentPub['link']

  // Iterate for multiples (e.g. numerators)
  let studyStats = [];
  for (var i = 0; i < publication.length; i++) {
    let num: number = publication[i]['Numerator']
    let numDesc: string = publication[i]['Numerator in-text']
    let apType: string = publication[i]['Priority AP Type']
    let denDesc: string = publication[i]['Denominator in-text']
    let statistic: string = publication[i]['Statistic']

    let stat = { "numDesc": numDesc, "num": num, "denDesc": denDesc, "apType": apType, "statistic": statistic }
    studyStats.push(stat);
  }

  // Initial value of content
  var description = `AT indicator(s) from this study: <br><ul class="publication__stats">`;

  // Loop numerators and produce bullets for each one.
  for (var i = 0; i < studyStats.length; i++) {
    description += `<li><b>${studyStats[i].statistic}%</b> of the ${studyStats[i].denDesc} ${studyStats[i].numDesc} ${studyStats[i].apType}</li>`;
  }

  if (link) {
    var cta = `<a class="button" target="_blank" href="${link}">Explore Publication</a>`;
  } else {
    var cta = ""
  }

  // Populate the content 
  let contentHTML = `<h3>${pubTitle}</h3><span class="participants">${participants} participants</span>`;
  contentDiv.innerHTML = contentHTML;
  let calloutHTML = `<p>${description}</ul></p> ${cta}`
  calloutDiv.innerHTML = calloutHTML;
}

// Function to update the publication area on change
export function updateDataArea(div: HTMLDivElement, newData: any) {
  // Clear current content
  div.innerHTML = "";

  // Filter the list by publication ID
  let dataByPub = groupByPublication(newData);


  if (dataByPub.length > 0) {
    // Create necessary containers for the vega and olli visualisations
    for (var i = 0; i < dataByPub.length; i++) {
      //Construct necessary HTML for injection
      let currentPublicationContainer = document.createElement("div");
      currentPublicationContainer.className = "publication__grid";

      let content = document.createElement("div");
      content.className = "publication__information";

      let callout = document.createElement("div");
      callout.className = "publication__callout";

      // Call populatePublicationInfo to populate content.
      populatePublicationInfo(content, callout, dataByPub[i].data);

      currentPublicationContainer.appendChild(content);
      currentPublicationContainer.appendChild(callout);

      div.appendChild(currentPublicationContainer);
    }
  } else {
    let currentPublicationContainer = document.createElement("div");
    currentPublicationContainer.className = "publication__grid";
    currentPublicationContainer.innerHTML = "<div class='no-data'>No data to display here, yet.<br><br>Have some research you think should be here? <br><br> <a href='https://airtable.com/shr6pTvQ4REb5iMYl' class='hyperlink'>Get in touch with us</a></div>"
    div.appendChild(currentPublicationContainer);
  }


  // Dispatch resize event to show responsive graphs
  window.dispatchEvent(new Event('resize'))
}

// Function to update the cohort / survey areas, where no vega / olli is needed
export function updateTableArea(div: HTMLDivElement, newData: any) {
  // Clear current content
  div.innerHTML = "";

  if (newData.length > 0) {
    // Create necessary containers for the vega and olli visualisations
    for (var i = 0; i < newData.length; i++) {
      let currentContentContainer = document.createElement("div");
      currentContentContainer.className = "survey__grid";
      let cohortDesc = newData[i]['Cohort Description'];
      let atTypes = humaniseAT(newData[i]['at-types']);
      let link = newData[i]['Link']

      //Construct necessary HTML for injection
      let content = document.createElement("div");

      if (!cohortDesc) {
        if (link) {
          var cta = `<a class="button" target="_blank" href="${link}">Explore Survey</a>`;
        } else {
          var cta = ""
        }
        content.className = "survey__information";
        content.innerHTML = `<h3>${newData[i]['Name']}</h3><p><span class='questionnaires'>${newData[i]['Questionnaires']} questionnaire(s), asked in </span><span class='years'>${newData[i]['Questionnaire Years']} into </span><span class='access-indicator'><b>'${newData[i]['AT Access Indicators']}'</b> for the technologies listed below.</span> ${atTypes}</p><br>${cta}`;
      } else {
        // Cohort Content
        if (link) {
          var cta = `<a class="button" target="_blank" href="${link}">Explore Cohort</a>`;
        } else {
          var cta = ""
        }
        content.className = "cohort__information";
        content.innerHTML = `<h3>${newData[i]['Name']}</h3><p>${cohortDesc}</p><span class='participants'>${newData[i]['Participants']} participants</span>${atTypes}<span class='other-at'><b>Other technologies included:</b> ${newData[i]['Other AT']}</span><br>${cta}`;
      }

      currentContentContainer.appendChild(content);
      div.appendChild(currentContentContainer);
    }
  } else {
    let currentContentContainer = document.createElement("div");
    currentContentContainer.className = "survey__grid";
    currentContentContainer.innerHTML = "<div class='no-data'>No data to display here, yet.<br><br>Have some research you think should be here? <br><br> <a href='https://airtable.com/shr6pTvQ4REb5iMYl' class='hyperlink'>Get in touch with us</a></div>"
    div.appendChild(currentContentContainer);
  }

}

// Function to update rata area
export function updateRataArea(div: HTMLDivElement, newData: any, currentCountry: String) {
  // Clear current content
  div.innerHTML = "";

  if (newData.length < 1 || newData[0].rataConducted == 0) {
    var desc = `<p>There <b>has not</b> been a rATA survey conducted in ${currentCountry}. You can explore findings from other rATA surveys on the WHO's <a href="https://www.who.int/data/gho/data/themes/assistivetech" target="_blank">Global Health Observatory</a> by selecting 'assistive technology progress indicators'.</p>`
  } else {
    var desc = `<p>There <b>has</b> been a rATA survey conducted in ${currentCountry}, using a ${newData[0].type} sample. You can explore findings from this and other rATA surveys on the WHO's <a href="https://www.who.int/data/gho/data/themes/assistivetech" target="_blank">Global Health Observatory</a> by selecting 'assistive technology progress indicators'.</p>`
  }
  div.innerHTML = desc
}

// function to change the active selector (e.g. country or AT)
export function updatePrimarySelector(selector: string, pubDataBySelector: any) {
  let activeSelectorData = [];

  if (selector === "Global") {
    pubDataBySelector.forEach((region: Array) => {
      region.data.forEach((publication) => {
        activeSelectorData.push(publication);
      });
    });

  } else {
    activeSelectorData = pubDataBySelector.filter(publications => publications.name === selector)[0].data;
    console.log(activeSelectorData)
  }


  return activeSelectorData;
}

// Function to populate AT filters
export function populateCountryFilters(activeData: any, select: HTMLSelectElement) {
  // Get types
  const currentCountries = [... new Set(activeData.map(country => country.Country))]
  currentCountries.sort()

  // Add all new ones
  for (var i = 0; i < currentCountries.length; i++) {
    var option = document.createElement("option");
    var currentCountry = currentCountries[i];
    var label = currentCountry;
    option.text = label.replace(/;/g, " /");
    option.value = currentCountry;
    select.add(option);
  }
}

// Function to populate AT filters
export function populateATFilters(activeData: any, select: HTMLSelectElement) {
  // Get types
  const currentATTypes = [... new Set(activeData.map(atType => atType['AP Type']))]

  // Add all new ones
  for (var i = 0; i < currentATTypes.length; i++) {
    var option = document.createElement("option");
    var currentAT = currentATTypes[i];
    var label = currentAT.slice(0, -7);
    option.text = label.replace(/;/g, " /");
    option.value = currentAT;
    select.add(option);
  }
}

// function to change the active filter
export function filterByCountry(country: string, activeData: any) {
  let filteredPublications = activeData.filter(datum => datum.Country === country)
  return filteredPublications;
}

// function to change the active filter
export function filterByAT(atType: string, activeData: any) {
  let filteredPublications = activeData.filter(datum => datum['AP Type'] === atType)
  return filteredPublications;
}

// Function to update the AT types includes in a country's dataset
export function updateFilters(activeData: any, select: HTMLSelectElement, filterType: any) {
  // Remove current ones
  select.innerHTML = '<option value="" disabled selected>Select...</option>'

  // Repopulate
  switch (filterType) {
    case "at": {
      populateATFilters(activeData, select);
      break;
    }
    case "country": {
      populateCountryFilters(activeData, select);
      break;
    }
    default: {
      console.log("Well, this is embarrassing")
      break;
    }
  }
}

// Generate an abstract for the country pages
export function generateCountryAbstract(activePubData: any, abstract: HTMLSpanElement, country: string, activeCohortData?: any, activeRataData?: any, activeSurveyData?: any, atType?: string) {
  // Filter the list by publication ID
  let dataByPub = groupByPublication(activePubData);
  let currentCountry = country.replace(/;/g, " /");
  currentCountry = toTitleCase(currentCountry);
  let abstractContent: string;
  let pubCount: any = dataByPub.length;

  if (atType) {
    let currentAT = atType.replace(/;/g, " /");
    currentAT = toTitleCase(currentAT).slice(0, -7);
    abstractContent = `${currentCountry} has <b>${pubCount}</b> published studies about ${currentAT}.`;
    abstract.innerHTML = abstractContent;
  } else {
    let cohortCount: any = activeCohortData.length > 0 ? activeCohortData.length : 0;
    let surveyCount: any = activeSurveyData.length > 0 ? activeSurveyData.length : 0;

    if (activeRataData.length < 1 || activeRataData[0].rataConducted == 0) {
      var rataStatus = 'has not conducted';
    } else {
      var rataStatus = 'has conducted';
    }

    if (country !== "Global") {
      abstractContent = `<p>${currentCountry} has <b>${pubCount}</b> published studies, <b>${surveyCount}</b> national survey(s), <b>${cohortCount}</b> cohort(s), and <b>${rataStatus}</b> a rapid Assistive Technology Assessment (rATA) survey including assistive technology data.</p>`
    } else {
      abstractContent = `<p>Globally, there has been <b>${pubCount}</b> published studies, <b>${surveyCount}</b> national survey(s), <b>${cohortCount}</b> cohort(s).`
    }


    abstract.innerHTML = abstractContent;
  }
}

// Generate an abstract for the AT pages
export function generateATAbstract(activeData: any, abstract: HTMLSpanElement, atType: string, country?: string) {
  // Filter the list by publication ID
  let dataByPub = groupByPublication(activeData);
  let count = dataByPub.length;
  let currentAT = atType.replace(/;/g, " /");
  currentAT = toTitleCase(currentAT).slice(0, -7);
  let abstractContent: string;

  if (count === 1) {
    abstractContent = count + " study has been published on " + currentAT
  } else {
    abstractContent = count + " studies have been published on " + currentAT
  }

  if (country) {
    let currentCountry = country.replace(/;/g, " /");
    currentCountry = toTitleCase(currentCountry);
    abstractContent = abstractContent + " in " + currentCountry + "."
  } else {
    abstractContent = abstractContent + " globally."
  }

  abstract.innerHTML = abstractContent;
}

//Generate the global stat summary
export function generateStatSummary(activeData: any, statContainer: HTMLSpanElement, atType: string) {
  let statContent: any;
  let currentAT = atType.replace(/;/g, " /");
  currentAT = toTitleCase(currentAT).slice(0, -7);

  if (activeData.length > 0) {
    // Create the stats list
    let list = document.createElement('ul');

    activeData.forEach((stat) => {
      // Create a list item and anchor element to display on the front end
      let listItem = document.createElement('li');
      let source = document.createElement('a');

      listItem.innerText = stat.statistics;

      source.innerText = "Source: " + stat.source
      source.setAttribute("href", stat.link);
      source.setAttribute("target", "_blank");

      listItem.appendChild(source);
      list.appendChild(listItem)
    });

    // Add it all into the stat container
    statContainer.innerHTML = "";
    statContainer.appendChild(list);

  } else {
    statContent = `No stats available yet on ${currentAT}`;
    statContainer.innerHTML = statContent;
  }


}


